import { useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import futureSecuritiesStore from "@/store/futureSecuritiesStore";
import tradeFromScratchStore from "@/store/tradeFromScratchStore";
import { sendFutureTicket } from "@/store/tradeFromScratchStoreACTIONS";
import FutureCoreFields from "./future/FutureCoreFields";
import FutureDetailFields from "./future/FutureDetailFields";
import { useFutureTicket } from "./future/useFutureTicket";
import {
  ReferenceDataGate,
  loadFuturesReferenceData,
  returnFuturesReferenceDataMessage,
  returnGateForStatuses,
} from "./helper/tradeFromScratchHelper";
import {
  returnHighlightedKeys,
  useTicketShell,
} from "./helper/useTicketShell";
import TicketStatusLine from "./ticketFields/TicketStatusLine";
import TicketHeader from "./ticketFields/TicketHeader";

// LQC-2494 - the Leg Creation future ticket. The contract comes from
// GET /Securities/Futures, the count is whole contracts, and settlement is the browser
// date (LQC-2496).

interface IFutureTicketProps {
  ticketId: string;
}

const FutureTicket = ({ ticketId }: IFutureTicketProps) => {
  const { user, getAccessTokenSilently } = useAuth0();
  const shell = useTicketShell(ticketId);
  const { missingEntries } = useFutureTicket(ticketId);
  const ensureFutureTicket = tradeFromScratchStore(
    (state) => state.ensureFutureTicket,
  );
  const resetFutureTicket = tradeFromScratchStore(
    (state) => state.resetFutureTicket,
  );
  const futureSecuritiesStatus = futureSecuritiesStore(
    (state) => state.futureSecuritiesStatus,
  );
  const gate = returnGateForStatuses([futureSecuritiesStatus]);

  // Fetched here rather than in the widget: a bond-only user never calls the endpoint.
  useEffect(() => {
    loadFuturesReferenceData(getAccessTokenSilently);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    ensureFutureTicket(ticketId);
  }, [ticketId, ensureFutureTicket]);

  const isReady = gate === ReferenceDataGate.Ready;
  const canSend = missingEntries.length === 0 && !shell.isSending && isReady;
  const highlightedKeys = returnHighlightedKeys(
    shell.isHoveringSend,
    canSend,
    missingEntries,
  );

  const handleSend = async () => {
    const sent = await sendFutureTicket({
      ticketId,
      user,
      getAccessTokenSilently,
    });
    if (sent) shell.markSent();
  };

  return (
    <div
      data-testid="FutureTicketContainer"
      id="FutureTicketContainer"
      className="flex h-full w-full flex-col gap-2"
    >
      {/* Renders while contracts load, so a failed fetch never traps the user here. */}
      <TicketHeader
        testIdPrefix="FutureTicket"
        instrument={shell.instrument}
        onInstrumentChange={shell.setInstrument}
        canSend={canSend}
        isSending={shell.isSending}
        isConfirming={shell.isConfirmingSend}
        onSend={handleSend}
        onClear={() => resetFutureTicket(ticketId)}
        onSendHoverChange={shell.setIsHoveringSend}
      />
      {isReady && (
        <TicketStatusLine
          testIdPrefix="FutureTicket"
          lastSent={shell.lastSent}
          missingFields={missingEntries.map((entry) => entry.label)}
          isBlocking={shell.isHoveringSend && !canSend}
        />
      )}

      {!isReady ? (
        <div
          data-testid="FutureTicketReferenceDataMessage"
          id="FutureTicketReferenceDataMessage"
          className="flex flex-1 flex-col items-center justify-center gap-2 text-sm opacity-60"
        >
          {returnFuturesReferenceDataMessage(gate)}
          {gate === ReferenceDataGate.Failed && (
            <button
              data-testid="FutureTicketRetryButton"
              id="FutureTicketRetryButton"
              className="rounded-full border border-gray-400 px-3 py-1 text-xs opacity-100"
              onClick={() =>
                loadFuturesReferenceData(getAccessTokenSilently, true)
              }
            >
              Retry
            </button>
          )}
        </div>
      ) : (
        <>
          <FutureCoreFields
            ticketId={ticketId}
            highlightedKeys={highlightedKeys}
          />
          <FutureDetailFields ticketId={ticketId} />
        </>
      )}
    </div>
  );
};

export default FutureTicket;
